// Public confirmation page — riders land here from email/SMS link.
import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle, XCircle, CalendarBlank, MapPin, Clock, Wheelchair } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { apiClient } from "../lib/api";
import { DUR, EASE } from "../lib/motion";
import { cue } from "../lib/soundCues";
import PageBackLink from "../components/PageBackLink";

export default function ConfirmTrip() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const initialAction = params.get("action"); // "confirm" or "decline" if magic link
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [showReschedule, setShowReschedule] = useState(false);
  const [newDate, setNewDate] = useState("");
  const [newTime, setNewTime] = useState("");

  useEffect(() => {
    apiClient.get(`/confirmation/${token}`)
      .then(({ data }) => setTrip(data))
      .catch(() => setTrip(null))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (initialAction && trip && !result && !submitting && ["confirm", "decline"].includes(initialAction)) {
      act(initialAction);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trip]);

  const act = async (action, extra = {}) => {
    setSubmitting(true);
    try { cue.confirm(); } catch { /* */ }
    try {
      const { data } = await apiClient.post(`/confirmation/${token}`, { action, ...extra });
      setResult({ action, ...data });
    } catch (e) {
      setResult({ action, error: e?.response?.data?.detail || "Failed" });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen grid place-items-center bg-bone text-sm text-slate-500">Loading…</div>;
  if (!trip) return (
    <div className="min-h-screen grid place-items-center bg-bone p-6">
      <div className="max-w-md text-center">
        <XCircle size={48} className="text-terracotta mx-auto" weight="duotone" />
        <h1 className="mt-4 font-heading text-2xl font-bold text-slate_navy">Link expired or not found</h1>
        <p className="mt-2 text-sm text-slate-600">Please call MedexOVA at <a href="tel:+18336592699" className="text-brandblue font-semibold">1-833-659-2699</a>.</p>
      </div>
    </div>
  );

  return (
    <main className="min-h-screen bg-bone py-12 px-4" data-testid="confirm-trip-page">
      <PageBackLink fallbackTo="/" label="Back to home" style={{ padding: "0 16px 16px" }} />
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-8">
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-brandblue">MedexOVA Medical Transportation</div>
          <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-black text-slate_navy">Confirm your trip</h1>
        </div>

        <div className="bg-white border border-[#E2E0D8] rounded-xl overflow-hidden shadow-sm" data-testid="confirm-trip-card">
          <div className="bg-slate_navy text-white px-6 py-4">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-brandblue">Ref #{trip.id.slice(0, 8).toUpperCase()}</div>
            <div className="mt-1 font-heading text-xl font-bold">{trip.patient_name}</div>
            <div className="text-xs text-white/60 capitalize">{(trip.trip_type || "").replace(/_/g, " ")} · {trip.mobility_type}</div>
          </div>
          <div className="p-6 space-y-3">
            <Row icon={<CalendarBlank size={16} className="text-forest" />} label="When">
              <b>{trip.pickup_date}</b> at <b>{trip.pickup_time}</b>
            </Row>
            <Row icon={<MapPin size={16} className="text-forest" />} label="Pickup">{trip.pickup_address}</Row>
            <Row icon={<MapPin size={16} className="text-terracotta" />} label="Drop-off">{trip.dropoff_address}</Row>
            {trip.return_trip && (
              <Row icon={<Clock size={16} className="text-brandblue" />} label="Return">Yes, round-trip</Row>
            )}
          </div>
        </div>

        {result ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.94, boxShadow: "0 0 0 rgba(0,0,0,0)" }}
            animate={{ opacity: 1, scale: 1, boxShadow: result.action === "confirm" ? "0 0 60px rgba(16,185,129,0.35)" : "0 0 60px rgba(220,38,38,0.35)" }}
            transition={{ duration: DUR.reveal, ease: EASE.pop }}
            className={`mt-6 rounded-xl border p-6 text-center ${
              result.action === "confirm" ? "border-forest/30 bg-forest/5" : "border-terracotta/30 bg-terracotta/5"
            }`} data-testid="confirm-result">
            {result.action === "confirm" ? <CheckCircle size={48} weight="duotone" className="text-forest mx-auto" /> : <XCircle size={48} weight="duotone" className="text-terracotta mx-auto" />}
            <h2 className="mt-3 font-heading text-xl font-bold text-slate_navy">
              {result.action === "confirm" ? "Trip confirmed" : result.action === "decline" ? "Trip cancelled" : "Reschedule requested"}
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              {result.action === "confirm"
                ? "You'll get a text from us when a driver is assigned."
                : result.action === "decline"
                ? "We've cancelled your trip. Call 1-833-659-2699 to rebook."
                : "A dispatcher will confirm the new time shortly."}
            </p>
          </motion.div>
        ) : (
          <div className="mt-6 grid sm:grid-cols-2 gap-3">
            <button
              onClick={() => act("confirm")}
              disabled={submitting}
              data-testid="btn-confirm"
              className="inline-flex items-center justify-center gap-2 bg-forest hover:bg-forest-hover text-white font-bold px-6 py-4 rounded-lg disabled:opacity-50 transition"
            >
              <CheckCircle size={18} weight="bold" /> Confirm trip
            </button>
            <button
              onClick={() => act("decline")}
              disabled={submitting}
              data-testid="btn-decline"
              className="inline-flex items-center justify-center gap-2 bg-white text-terracotta border border-terracotta hover:bg-terracotta/5 font-bold px-6 py-4 rounded-lg disabled:opacity-50 transition"
            >
              <XCircle size={18} weight="bold" /> Can't make it
            </button>
            <button
              onClick={() => setShowReschedule((v) => !v)}
              className="sm:col-span-2 text-sm text-brandblue hover:text-brandblue-hover font-semibold py-2"
              data-testid="btn-reschedule-toggle"
            >
              Need a different time?
            </button>
            {showReschedule && (
              <div className="sm:col-span-2 bg-white border border-[#E2E0D8] rounded-lg p-4 space-y-3" data-testid="reschedule-form">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-slate_navy mb-1">New date</label>
                    <input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="w-full border border-[#E2E0D8] rounded-md px-3 py-2 text-sm" />
                  </div>
                  <div>
                    <label className="block text-[10px] uppercase tracking-[0.2em] font-bold text-slate_navy mb-1">New time</label>
                    <input type="time" value={newTime} onChange={(e) => setNewTime(e.target.value)} className="w-full border border-[#E2E0D8] rounded-md px-3 py-2 text-sm" />
                  </div>
                </div>
                <button
                  onClick={() => act("reschedule", { new_date: newDate, new_time: newTime })}
                  disabled={submitting || !newDate || !newTime}
                  data-testid="btn-reschedule-submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-brandblue text-white font-bold px-4 py-2.5 rounded-md disabled:opacity-50"
                >
                  Request reschedule
                </button>
              </div>
            )}
          </div>
        )}

        <p className="text-center text-xs text-slate-500 mt-8">
          Need help? Call <a href="tel:+18336592699" className="text-brandblue font-semibold">1-833-659-2699</a>
        </p>
      </div>
    </main>
  );
}

function Row({ icon, label, children }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5">{icon}</div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400">{label}</div>
        <div className="text-sm text-slate_navy">{children}</div>
      </div>
    </div>
  );
}
